/**
 * 骨架屏与空状态。
 *
 * 骨架屏的形状必须和最终内容一致：形状不对的骨架比转圈还让人困惑，
 * 因为它在暗示一个不存在的布局。
 *
 * 空状态不是"暂无数据"四个字，它要回答"接下来我能做什么"。
 */

import type { ReactNode } from 'react';

import { GLYPHS, IconGlyph } from './IconGlyph';

/** 一行卡片形状的骨架，用于冰箱列表 */
export function SkeletonRow({ count = 4 }: { count?: number }): JSX.Element {
	return (
		<div className="group divide-y divide-separator">
			{Array.from({ length: count }).map((_, index) => (
				<div key={index} className="flex items-center gap-3 px-4 py-4">
					<div className="flex-1">
						<div className="skeleton h-4" style={{ width: `${52 + ((index * 13) % 28)}%` }} />
						<div className="skeleton mt-2 h-3 w-24" />
					</div>
					<div className="skeleton h-7 w-16 rounded-control" />
				</div>
			))}
		</div>
	);
}

/** 菜谱卡片形状的骨架 */
export function SkeletonRecipe({ count = 2 }: { count?: number }): JSX.Element {
	return (
		<div className="flex flex-col gap-3">
			{Array.from({ length: count }).map((_, index) => (
				<div key={index} className="surface p-4">
					<div className="skeleton h-5 w-32" />
					<div className="skeleton mt-2.5 h-3.5 w-4/5" />
					<div className="mt-3 flex gap-2">
						<div className="skeleton h-6 w-20 rounded-control" />
						<div className="skeleton h-6 w-16 rounded-control" />
						<div className="skeleton h-6 w-14 rounded-control" />
					</div>
					<div className="skeleton mt-3 h-14" />
				</div>
			))}
		</div>
	);
}

export interface EmptyStateProps {
	glyph: Parameters<typeof IconGlyph>[0]['glyph'];
	title: string;
	hint?: ReactNode;
	action?: ReactNode;
}

export function EmptyState({ glyph, title, hint, action }: EmptyStateProps): JSX.Element {
	return (
		<div className="flex flex-col items-center px-8 py-16 text-center">
			<div className="flex h-16 w-16 items-center justify-center rounded-card bg-surface-2">
				<IconGlyph glyph={glyph} size={28} className="text-label-3" />
			</div>
			<p className="mt-4 text-[15px] font-medium text-label">{title}</p>
			{hint ? (
				<p className="mt-1.5 max-w-[26ch] text-[13px] leading-relaxed text-label-2">{hint}</p>
			) : null}
			{action ? <div className="mt-5">{action}</div> : null}
		</div>
	);
}
